import JSZip from "jszip";

const DOCX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const XML_NAMESPACE = "http://www.w3.org/XML/1998/namespace";
const WORD_NAMESPACE =
  "http://schemas.openxmlformats.org/wordprocessingml/2006/main";
const TEMPLATE_VARIABLE_PATTERN = /\{\{([^{}]+)\}\}/g;
const HAS_TEMPLATE_VARIABLE_PATTERN = /\{\{[^{}]+\}\}/;
const BORDER_SIDES = ["top", "left", "bottom", "right", "start", "end"];
export const BROWSER_PREVIEW_VARIABLE_MARKER = "LW_PREVIEW_VARIABLE:";

type TextSegment = {
  element: Element;
  end: number;
  start: number;
  text: string;
};

const getTextSegments = (paragraph: Element): TextSegment[] => {
  let offset = 0;

  return Array.from(paragraph.getElementsByTagNameNS("*", "t")).map(
    (element) => {
      const text = element.textContent ?? "";
      const segment = {
        element,
        start: offset,
        end: offset + text.length,
        text,
      };
      offset = segment.end;
      return segment;
    },
  );
};

const setText = (element: Element, value: string) => {
  element.textContent = value;
  if (/^\s|\s$/.test(value)) {
    element.setAttributeNS(XML_NAMESPACE, "xml:space", "preserve");
  }
};

const getParentRun = (element: Element): Element | null => {
  let parent = element.parentElement;
  while (parent && parent.localName !== "r") parent = parent.parentElement;
  return parent;
};

const getDirectChild = (element: Element, localName: string) =>
  Array.from(element.children).find((child) => child.localName === localName);

const getOrCreateProperties = (
  element: Element,
  propertiesName: string,
): Element => {
  const existing = getDirectChild(element, propertiesName);
  if (existing) return existing;

  const properties = element.ownerDocument.createElementNS(
    WORD_NAMESPACE,
    `w:${propertiesName}`,
  );
  element.insertBefore(properties, element.firstChild);
  return properties;
};

const setBordersToNone = (properties: Element, bordersName: string) => {
  let borders = getDirectChild(properties, bordersName);
  if (!borders) {
    borders = properties.ownerDocument.createElementNS(
      WORD_NAMESPACE,
      `w:${bordersName}`,
    );
    properties.appendChild(borders);
  }

  BORDER_SIDES.forEach((side) => {
    let border = getDirectChild(borders, side);
    if (!border) {
      border = properties.ownerDocument.createElementNS(
        WORD_NAMESPACE,
        `w:${side}`,
      );
      borders.appendChild(border);
    }
    border.setAttributeNS(WORD_NAMESPACE, "w:val", "nil");
  });
};

const getAncestor = (element: Element, localName: string): Element | null => {
  let ancestor = element.parentElement;
  while (ancestor && ancestor.localName !== localName) {
    ancestor = ancestor.parentElement;
  }
  return ancestor;
};

const suppressPreviewOnlyBorders = (paragraph: Element) => {
  setBordersToNone(getOrCreateProperties(paragraph, "pPr"), "pBdr");

  const tableCell = getAncestor(paragraph, "tc");
  const table = tableCell ? getAncestor(tableCell, "tbl") : null;
  if (
    !tableCell ||
    !table ||
    table.getElementsByTagNameNS("*", "tc").length !== 1
  ) {
    return;
  }

  setBordersToNone(getOrCreateProperties(table, "tblPr"), "tblBorders");
  setBordersToNone(getOrCreateProperties(tableCell, "tcPr"), "tcBorders");
};

const shadeRun = (run: Element, fill: string) => {
  const document = run.ownerDocument;
  let runProperties = Array.from(run.children).find(
    ({ localName }) => localName === "rPr",
  );
  if (!runProperties) {
    runProperties = document.createElementNS(WORD_NAMESPACE, "w:rPr");
    run.insertBefore(runProperties, run.firstChild);
  }

  getDirectChild(runProperties, "highlight")?.remove();
  let shading = Array.from(runProperties.children).find(
    ({ localName }) => localName === "shd",
  );
  if (!shading) {
    shading = document.createElementNS(WORD_NAMESPACE, "w:shd");
    runProperties.appendChild(shading);
  }
  shading.setAttributeNS(WORD_NAMESPACE, "w:val", "clear");
  shading.setAttributeNS(WORD_NAMESPACE, "w:fill", fill);
};

const insertTextRun = (
  sourceRun: Element,
  text: string,
  fill?: string,
): Element | null => {
  if (!text) return null;

  const run = sourceRun.cloneNode(false) as Element;
  const sourceProperties = Array.from(sourceRun.children).find(
    ({ localName }) => localName === "rPr",
  );
  if (sourceProperties) run.appendChild(sourceProperties.cloneNode(true));

  const textElement = sourceRun.ownerDocument.createElementNS(
    WORD_NAMESPACE,
    "w:t",
  );
  setText(textElement, text);
  run.appendChild(textElement);
  if (fill) shadeRun(run, fill);
  sourceRun.parentNode?.insertBefore(run, sourceRun);
  return run;
};

const splitRunWithHighlight = (
  run: Element,
  text: string,
  highlightStart: number,
  highlightEnd: number,
) => {
  insertTextRun(run, text.slice(0, highlightStart));
  insertTextRun(run, text.slice(highlightStart, highlightEnd), "FECACA");
  insertTextRun(run, text.slice(highlightEnd));
  run.remove();
};

const insertVariableMarker = (sourceRun: Element, variable: string) => {
  const markerRun = insertTextRun(
    sourceRun,
    `${BROWSER_PREVIEW_VARIABLE_MARKER}${encodeURIComponent(variable)}`,
  );
  if (!markerRun) return;

  const properties = getOrCreateProperties(markerRun, "rPr");
  const vanish = properties.ownerDocument.createElementNS(
    WORD_NAMESPACE,
    "w:vanish",
  );
  vanish.setAttributeNS(WORD_NAMESPACE, "w:val", "true");
  properties.appendChild(vanish);
};

const markFilledText = (
  textElement: Element,
  valueStart: number,
  valueLength: number,
  variable: string,
) => {
  const run = getParentRun(textElement);
  if (!run) return;

  const runTextElements = Array.from(run.getElementsByTagNameNS("*", "t"));
  const textBeforeElement = runTextElements
    .slice(0, runTextElements.indexOf(textElement))
    .reduce(
      (length, element) => length + (element.textContent?.length ?? 0),
      0,
    );
  const runText = runTextElements
    .map((element) => element.textContent ?? "")
    .join("");
  const start = textBeforeElement + valueStart;
  const end = start + valueLength;

  insertTextRun(run, runText.slice(0, start));
  insertVariableMarker(run, variable);
  insertTextRun(run, runText.slice(start, end), "BBF7D0");
  insertTextRun(run, runText.slice(end));
  run.remove();
};

const highlightTextRange = (
  paragraph: Element,
  rangeStart: number,
  rangeEnd: number,
) => {
  const segments = getTextSegments(paragraph);
  const intersectingRuns = new Set(
    segments
      .filter(({ start, end }) => end > rangeStart && start < rangeEnd)
      .map(({ element }) => getParentRun(element))
      .filter((run): run is Element => run !== null),
  );

  intersectingRuns.forEach((run) => {
    const runSegments = segments.filter(
      ({ element }) => getParentRun(element) === run,
    );
    const firstSegment = runSegments[0];
    const lastSegment = runSegments[runSegments.length - 1];

    const runText = runSegments.map(({ text }) => text).join("");
    const highlightStart =
      Math.max(rangeStart, firstSegment.start) - firstSegment.start;
    const highlightEnd =
      Math.min(rangeEnd, lastSegment.end) - firstSegment.start;
    splitRunWithHighlight(run, runText, highlightStart, highlightEnd);
  });
};

const replaceParagraphVariables = (
  paragraph: Element,
  inputs: Record<string, string>,
  interactive: boolean,
) => {
  const initialSegments = getTextSegments(paragraph);
  const paragraphText = initialSegments.map(({ text }) => text).join("");
  const matches = Array.from(paragraphText.matchAll(TEMPLATE_VARIABLE_PATTERN));

  // Work backwards so replacements later in the paragraph do not move the
  // offsets of variables that appear before them.
  matches.reverse().forEach((match) => {
    const variable = match[1].trim();
    const replacement = variable ? inputs[variable] : undefined;
    const matchStart = match.index;
    if (!variable) return;

    const matchEnd = matchStart + match[0].length;
    if (replacement === undefined) {
      if (interactive) highlightTextRange(paragraph, matchStart, matchEnd);
      return;
    }

    const segments = getTextSegments(paragraph);
    const startIndex = segments.findIndex(
      ({ start, end }) => start <= matchStart && end > matchStart,
    );
    const endIndex = segments.findIndex(
      ({ start, end }) => start < matchEnd && end >= matchEnd,
    );
    if (startIndex < 0 || endIndex < 0) return;

    const startSegment = segments[startIndex];
    const endSegment = segments[endIndex];

    const prefix = startSegment.text.slice(0, matchStart - startSegment.start);
    const suffix = endSegment.text.slice(matchEnd - endSegment.start);

    if (startIndex === endIndex) {
      setText(startSegment.element, `${prefix}${replacement}${suffix}`);
      if (interactive) {
        markFilledText(
          startSegment.element,
          prefix.length,
          replacement.length,
          variable,
        );
      }
      return;
    }

    setText(startSegment.element, `${prefix}${replacement}`);
    for (let index = startIndex + 1; index < endIndex; index += 1) {
      const segment = segments[index];
      setText(segment.element, "");
    }
    setText(endSegment.element, suffix);
    if (interactive) {
      markFilledText(
        startSegment.element,
        prefix.length,
        replacement.length,
        variable,
      );
    }
  });
};

const mergeDocumentXml = (
  xml: string,
  inputs: Record<string, string>,
  interactive: boolean,
): string => {
  const document = new DOMParser().parseFromString(xml, "application/xml");
  if (document.getElementsByTagName("parsererror").length > 0) return xml;

  Array.from(document.getElementsByTagNameNS("*", "p")).forEach((paragraph) => {
    const paragraphText = getTextSegments(paragraph)
      .map(({ text }) => text)
      .join("");
    if (HAS_TEMPLATE_VARIABLE_PATTERN.test(paragraphText)) {
      suppressPreviewOnlyBorders(paragraph);
    }
    replaceParagraphVariables(paragraph, inputs, interactive);
  });

  return new XMLSerializer().serializeToString(document);
};

export const buildBrowserDocxPreview = async (
  template: ArrayBuffer,
  inputs: Record<string, string>,
  options?: { interactive?: boolean },
): Promise<Blob> => {
  const interactive = options?.interactive ?? true;
  const zip = await JSZip.loadAsync(template);
  const xmlFiles = Object.values(zip.files).filter(
    (file) =>
      !file.dir &&
      /^word\/(?:document|header\d*|footer\d*|footnotes|endnotes)\.xml$/.test(
        file.name,
      ),
  );

  await Promise.all(
    xmlFiles.map(async (file) => {
      const xml = await file.async("string");
      zip.file(file.name, mergeDocumentXml(xml, inputs, interactive));
    }),
  );

  return zip.generateAsync({ type: "blob", mimeType: DOCX_MIME_TYPE });
};
